import ReactDom from 'react-dom';
import styled from '@emotion/styled';
import { css, cx } from '@emotion/css';
import { FramelessModal, Mask } from './index';
import reportImg from './report.webp';
import { CloseOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useAiReplyReportValue, useGotoAIReplyReport } from 'hooks/business/useAIReplyReport';
import { useCurrencyTotalFormat } from '@knowledge/Activity/hooks/useCurrencyTotalDisplay';
import { durationFormat } from 'utils/format';
import { formatNumber } from 'utils/numberFormat';

interface AIReplyReportModalProps {
  reportId: number;
  visible?: boolean;
  onClose?: () => void;
}

export const AIReplyReportModal = (props: AIReplyReportModalProps) => {
  const { reportId, visible, onClose } = props;
  const { t } = useTranslate();
  const gotoAIReplyReport = useGotoAIReplyReport();
  const report = useAiReplyReportValue();

  const currencyTotalFormat = useCurrencyTotalFormat();

  return visible && report
    ? ReactDom.createPortal(
        <>
          <FramelessModal>
            <Frame className="min-w-[560px] rounded-lg flex-col">
              <div className="flex h-8 px-3 items-center justify-end">
                <span className="w-8 h-8 cursor-pointer" onClick={onClose}>
                  <CloseOutlined className="!text-[var(--fett-text-tertiary)]  hover:!text-[var(--fett-text-secondary)]" />
                </span>
              </div>
              <div className="px-4 w-full relative">
                <img className="w-28 absolute -top-20 right-12" src={reportImg} alt="report" />
                <div className="bg-[var(--fett-bg-panel)] opacity-75 rounded p-4">
                  <p className="font-medium text-base text-[var(--fett-text-primary)]">
                    {t('小乐已为您生成了使用AI精准回复后的使用报告')}
                  </p>
                  <div className={cx(statsStrip, 'rounded flex flex-row items-center')}>
                    <div className="flex-1 p-4">
                      <div>{t('AI回复问题总数')}</div>
                      <div className="font-medium text-xl mt-2">
                        {formatNumber(report.aiReplyQueryCount, '0,0')}
                      </div>
                    </div>
                    <div className="flex-1 p-4">
                      <div>{t('使用AI前/后平均响应时长')}</div>
                      <div className="font-medium text-xl mt-2">
                        {report?.beforeRespTime ? durationFormat(report.beforeRespTime) : '-'}
                        <span>/</span>
                        {report?.afterRespTime ? durationFormat(report.afterRespTime) : '-'}
                      </div>
                    </div>
                    <div className="flex-1 p-4">
                      <div>{t('AI独立接待买家下单金额')}</div>
                      <div className="font-medium text-xl mt-2 text-nowrap">
                        {currencyTotalFormat(report.aiAloneReceptionBuyerOrderAmountList)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end p-4 gap-2">
                <Button type="text" className="!text-[var(--fett-text-tertiary)]" onClick={onClose}>
                  {t('稍后查看')}
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    onClose?.();
                    gotoAIReplyReport(reportId);
                  }}
                >
                  {t('前往查看详细报告')}
                </Button>
              </div>
            </Frame>
          </FramelessModal>
          <Mask />
        </>,
        document.body,
      )
    : null;
};

const Frame = styled.div`
  background-image: linear-gradient(180deg, #c2d9ff 0%, var(--fett-bg-panel) 100%);
  /* dark：浅色渐变起点换成 dark 选中面（蓝灰），保持「顶部微亮渐入面板」层次 */
  [data-color-mode='dark'] & {
    background-image: linear-gradient(180deg, var(--fett-bg-active) 0%, var(--fett-bg-panel) 100%);
  }
`;

/* 数据条：light 保持 #E6EEFF 浅蓝；dark 语义映射到选中面（蓝灰），避免浅底上浅色文字不可读 */
const statsStrip = css`
  background: #e6eeff;
  [data-color-mode='dark'] & {
    background: var(--fett-bg-active);
  }
`;
